<!-- Seadmed: the equipment installed at an address — the boiler, the water
     heater — one row each, with its serial number and when it went in.

     It loads its own list rather than reading the object payload it is handed,
     so adding a device does not need the whole object page to be re-fetched.
     Who may edit is the caller's to say (the server decides; this only hides
     buttons that would be refused) — the office and whoever entered the address,
     and the technicians, who are the ones who read a serial number off a plate.

     Two ways for a device to go. Arhiveeri is what happens to a boiler that was
     replaced: it leaves the object page, the work order, the act and the
     customer's view, and stays here — greyed out, with Taasta. Kustuta is for a
     row that should never have existed, which is an office correction rather
     than a field one — hence `deletable` on top of `editable`: a technician may
     keep the list true, only the object's own people may make a row never have
     been. `showArchived` is the Seadmed page, which is the only screen that
     shows the archived ones at all. -->
<template>
  <v-card variant="flat" border>
    <!-- d-flex: Vuetify 2's card title was a flex row, which is what the
         spacer pushes the button across. -->
    <v-card-title class="text-subtitle-1 py-2 d-flex align-center">
      Seadmed
      <v-spacer />
      <v-btn v-if="editable" color="primary" @click="open()" size="small" variant="text">
        <v-icon size="small" start>mdi-plus</v-icon>Lisa seade
      </v-btn>
    </v-card-title>
    <v-divider />
    <v-data-table
      :headers="headers"
      :items="rows"
      :loading="loading"
      hide-default-footer
      :items-per-page="-1"
      no-data-text="Seadmeid pole lisatud"
      loading-text="Laen..."
    >
      <!-- Archived rows sit at the bottom of the same list, greyed, with the
           chip _notes.vue uses to mark a note the customer cannot see. A second
           table would have been a second list to read. -->
      <template v-slot:[`item.name`]="{item}">
        <span :class="{ 'text-grey': item.archived }">{{item.name}}</span>
        <v-chip v-if="item.archived" color="grey" class="ml-2" size="x-small" variant="flat">Arhiveeritud</v-chip>
      </template>
      <template v-slot:[`item.installedAt`]="{item}">
        <span :class="{ 'text-grey': item.archived }">{{format(item.installedAt)}}</span>
      </template>
      <template v-slot:[`item.serial`]="{item}">
        <span :class="{ 'text-grey': item.archived }">{{item.serial || '—'}}</span>
      </template>
      <template v-slot:[`item.notes`]="{item}">
        <span :class="{ 'text-grey': item.archived }">{{item.notes || '—'}}</span>
      </template>
      <template v-slot:[`item.actions`]="{item}">
        <template v-if="editable && item.archived">
          <v-btn color="green-darken-2" @click="restore(item)" size="x-small" variant="text">Taasta</v-btn>
          <v-btn v-if="deletable" color="red" @click="confirmDelete = item" size="x-small" variant="text">Kustuta</v-btn>
        </template>
        <template v-else-if="editable">
          <v-btn @click="open(item)" size="x-small" variant="text">Muuda</v-btn>
          <v-btn color="orange-darken-3" @click="archive(item)" size="x-small" variant="text">Arhiveeri</v-btn>
          <v-btn v-if="deletable" color="red" @click="confirmDelete = item" size="x-small" variant="text">Kustuta</v-btn>
        </template>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="text-h5">{{form.id ? 'Muuda seadet' : 'Lisa seade'}}</v-card-title>
        <v-card-text>
          <v-text-field v-model="form.name" label="Seade*" :error-messages="nameError" />
          <v-text-field v-model="form.serial" label="Seerianumber" />
          <datepicker v-model="form.installedAt" label="Paigaldatud" />
          <v-textarea v-model="form.notes" label="Märkus" rows="2" auto-grow hide-details />
          <!-- The office writes things for itself here; the customer's view and
               the act's PDF both leave this field out. -->
          <div class="text-caption text-grey mt-1">Märkust kliendile ei näidata.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="dialog = false" :disabled="saving" variant="text">Tühista</v-btn>
          <v-btn color="success" :loading="saving" @click="save()">Salvesta</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <confirm @cancel="confirmDelete = null" @confirm="remove(confirmDelete)" :visible="!!confirmDelete"
      heading="Kustuta seade?" text="Seade kaob objekti juurest jäädavalt. Kui see on lihtsalt maha võetud, arhiveeri see." action="Kustuta seade"
      :detail="confirmDelete ? confirmDelete.name : ''" :detail-sub="confirmDelete && confirmDelete.serial ? 'Seerianumber ' + confirmDelete.serial : ''" />
  </v-card>
</template>

<script>
import api from '@/api'
import dayjs from 'dayjs'
import { mapActions } from 'vuex'

const empty = { id: null, name: '', serial: '', installedAt: '', notes: '' }

export default {
  emits: ['changed'],
  props: {
    objectId: {
      required: true
    },
    editable: {
      type: Boolean,
      default: false
    },
    // Kustuta, which not everyone who may edit the list may do — the server
    // keeps deleting to whoever may edit the object itself.
    deletable: {
      type: Boolean,
      default: false
    },
    // The Seadmed page asks for the archived rows too; everywhere else this
    // component appears, only what is installed now belongs on screen.
    showArchived: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      devices: [],
      loading: true,
      saving: false,
      dialog: false,
      confirmDelete: null,
      nameError: '',
      form: { ...empty },
      headers: [
        { title: 'Seade', value: 'name', sortable: false },
        { title: 'Seerianumber', value: 'serial', sortable: false },
        { title: 'Paigaldatud', value: 'installedAt', sortable: false },
        { title: 'Märkus', value: 'notes', sortable: false },
        { title: '', value: 'actions', sortable: false, align: 'end' }
      ]
    }
  },
  computed: {
    active () {
      return this.devices.filter(device => !device.archived)
    },
    // Installed first, replaced last — the office reads the list top down and
    // what is there now is what it came for.
    rows () {
      return [...this.active, ...this.devices.filter(device => device.archived)]
    }
  },
  watch: {
    objectId () {
      this.refresh()
    }
  },
  methods: {
    ...mapActions(['showNotification']),
    async refresh () {
      // Leaving the page clears the route's objectId before this list is gone.
      if (!this.objectId) {
        return
      }
      this.loading = true
      try {
        const { devices } = await api.getObjectDevices(this.objectId, this.showArchived ? { archived: true } : {})
        this.devices = devices
        // Whoever embedded this may be showing a summary of its own — the work
        // order does — and it has no other way to know the list moved.
        this.$emit('changed', this.active)
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      this.loading = false
    },
    format (date) {
      return date ? dayjs(date).format('DD.MM.YYYY') : '—'
    },
    open (device) {
      this.nameError = ''
      this.form = device
        ? { id: device.id, name: device.name, serial: device.serial || '', installedAt: device.installedAt || '', notes: device.notes || '' }
        : { ...empty }
      this.dialog = true
    },
    async save () {
      const name = (this.form.name || '').trim()
      if (!name) {
        this.nameError = 'Seadme nimetus on kohustuslik'
        return
      }
      this.saving = true
      const body = {
        name,
        serial: (this.form.serial || '').trim(),
        installedAt: this.form.installedAt || '',
        notes: (this.form.notes || '').trim()
      }
      try {
        if (this.form.id) {
          await api.updateObjectDevice(this.objectId, this.form.id, body)
        } else {
          await api.createObjectDevice(this.objectId, body)
        }
        this.dialog = false
        this.showNotification({ text: 'Seade salvestatud', color: 'success' })
        await this.refresh()
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      this.saving = false
    },
    // Archiving asks nothing: it takes nothing away that Taasta cannot put
    // back, and on a screen that does not show the archived rows the device
    // simply leaves the list — which is what was asked for.
    async archive (device) {
      this.loading = true
      try {
        await api.archiveObjectDevice(this.objectId, device.id)
        this.showNotification({ text: 'Seade arhiveeritud', color: 'success' })
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      await this.refresh()
    },
    async restore (device) {
      this.loading = true
      try {
        await api.restoreObjectDevice(this.objectId, device.id)
        this.showNotification({ text: 'Seade taastatud', color: 'success' })
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      await this.refresh()
    },
    async remove (device) {
      this.confirmDelete = null
      this.loading = true
      try {
        await api.deleteObjectDevice(this.objectId, device.id)
        this.showNotification({ text: 'Seade eemaldatud', color: 'success' })
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      await this.refresh()
    }
  },
  mounted () {
    this.refresh()
  }
}
</script>
